// types
import {
	Account,
	Conversion,
	JsonObject,
	Payment,
	Transaction,
	TransactionGroup,
	TransactionGroupItem,
	TransactionGroupType,
} from '../@types';

// i18n
import { TFunction } from 'i18next';

export type Group = {
	key: string;
	selector: (transaction: TransactionGroup) => string;
};

export interface GroupDataParams {
	currency: string | undefined;
	groupKeys: string[];
	hasTax?: boolean;
	monthHeaders: string[];
	t: TFunction;
	transactionsGrouping: TransactionGroup[];
}

export const groupTransactions = ({
	currency,
	groupKeys,
	hasTax = true,
	monthHeaders,
	transactionsGrouping,
	t,
}: GroupDataParams): TransactionGroupItem[] => {
	if (!currency) {
		return [];
	}

	// define grouping logic for transactions
	const groups: Group[] = [
		{ key: 'component', selector: (t) => t.component?.name || '' },
		{ key: 'category', selector: (t) => t.category?.name || '' },
		{ key: 'expenseType', selector: (t) => t.expenseType?.name || '' },
	];

	// define default indent level
	let indent = 0;

	// Generate the full range of months
	const fullMonthRange = getFullMonthRange(transactionsGrouping);

	// recursively group transactions based on selected group keys
	const groupTransactions = (
		transactions: TransactionGroup[],
		groupKeys: string[]
	): TransactionGroupItem[] => {
		// Exit if no group keys
		if (groupKeys?.length === 0) {
			return [];
		}

		const groupKey = groupKeys[0];
		const group = groups.find((g) => g.key === groupKey);

		// Exit if group key is invalid
		if (!group) {
			return [];
		}

		// initialize grouped data structure
		const grouped: Record<string, TransactionGroupItem> = {};

		// add each group
		transactions.forEach((tx) => {
			// get the key for the current group
			const key = group.selector(tx);

			// if the key is empty, skip this transaction
			if (!grouped[key]) {
				grouped[key] = {
					children: [],
					currency,
					indent,
					isTotalRow: false,
					name: tx[group.key as TransactionGroupType]?.name,
					total: 0,
					totalPaidFromDa: 0,
				} as TransactionGroupItem;
			}

			// add all months to each group
			Object.keys(grouped).forEach((key) => {
				fullMonthRange.forEach((YYYY_MM: string) => {
					if (!grouped[key][YYYY_MM]) {
						grouped[key][YYYY_MM] = 0;
					}
				});
			});

			// format the transaction year and month
			const YYYY_MM = `${tx.year}-${String(tx.month).padStart(2, '0')}`;

			grouped[key].type = group.key as TransactionGroupType;
			grouped[key].name = tx[group.key as TransactionGroupType]?.name;

			if (tx.currency === currency) {
				const net = tx.sum + (hasTax ? tx.sumTax : 0) - (tx.sumRefunded || 0);
				(grouped[key][YYYY_MM] as number) += net;
				grouped[key].total += net;
				grouped[key].currency = tx.currency;
			}

			// add equivalent amounts if transaction designated account currency matches
			if (tx.designatedAccount && (tx.designatedAccount as Account)?.currency === currency) {
				let conversions: Conversion[] = [];

				// collect all conversions from transaction payments
				for (const txItem of (tx.transactions || []) as Transaction[]) {
					for (const payment of (txItem.payments || []) as Payment[]) {
						conversions = [...conversions, ...((payment.conversions || []) as Conversion[])];
					}
				}

				// filter conversions by currency
				const conversionsByCurrency = conversions.filter((c) => c.currency === currency);

				// calculate equivalent sums
				const sumEquivalent = conversionsByCurrency.reduce(
					(total, c) => total + (c.amount || 0),
					0
				);
				const sumTaxEquivalent = conversionsByCurrency.reduce(
					(total, c) => total + (c.tax || 0),
					0
				);

				const totalDaEquivalent = sumEquivalent + (hasTax ? sumTaxEquivalent : 0);

				// calculate DA equivalent of refunds from refund payment conversions
				let refundConversions: Conversion[] = [];
				for (const refund of (tx.refunds || []) as Transaction[]) {
					for (const payment of (refund.payments || []) as Payment[]) {
						refundConversions = [
							...refundConversions,
							...((payment.conversions || []) as Conversion[]),
						];
					}
				}
				const refundConversionsByCurrency = refundConversions.filter(
					(c) => c.currency === currency
				);
				const refundDaEquivalent = refundConversionsByCurrency.reduce(
					(total, c) => total + (c.amount || 0) + (hasTax ? c.tax || 0 : 0),
					0
				);

				// update grouped totals
				(grouped[key][YYYY_MM] as number) += totalDaEquivalent - refundDaEquivalent;
				grouped[key].total += totalDaEquivalent - refundDaEquivalent;
				grouped[key].totalPaidFromDa =
					(grouped[key].totalPaidFromDa || 0) + totalDaEquivalent - refundDaEquivalent;
			} else {
				// set totalPaidFromDa to total if no designated account match
				grouped[key].totalPaidFromDa = grouped[key].total || 0;
			}
		});

		// increment indent for child groups
		indent++;

		const groupedKeys = Object.keys(grouped);

		// round totals and recursively group children
		groupedKeys.forEach((key) => {
			grouped[key].total = Number(grouped[key].total.toFixed(2));
			grouped[key].totalPaidFromDa = Number(grouped[key].totalPaidFromDa.toFixed(2));
			grouped[key].children = groupTransactions(
				transactions.filter((t) => group.selector(t) === key),
				groupKeys.slice(1)
			);
		});

		// decrement indent for total row
		indent--;

		const totalRow: TransactionGroupItem = {
			children: [],
			currency,
			indent,
			isTotalRow: true,
			name: indent === 0 ? t('grandTotal') : t('total'),
			total: 0,
			totalPaidFromDa: 0,
			type: group.key as TransactionGroupType,
		};

		groupedKeys.forEach((key, index) => {
			const isLast = index === groupedKeys.length - 1;

			// sum totals for the total row
			totalRow.total += grouped[key].total;
			totalRow.totalPaidFromDa =
				(totalRow.totalPaidFromDa || 0) + (grouped[key].totalPaidFromDa || 0);

			// sum each month for the total row
			monthHeaders.forEach((monthYear: string) => {
				if (!totalRow[monthYear]) totalRow[monthYear] = 0;
				(totalRow[monthYear] as number) += (grouped[key][monthYear] || 0) as number;
			});

			// round totals on the last iteration
			if (isLast) {
				totalRow.total = Number(totalRow.total.toFixed(2));
				totalRow.totalPaidFromDa = Number(totalRow.totalPaidFromDa.toFixed(2));
			}
		});

		return [...Object.values(grouped), totalRow];
	};
	return groupTransactions(transactionsGrouping, groupKeys);
};

export interface GroupHeadersParams {
	transactionsGrouping: TransactionGroup[];
}
export const getGroupDateHeaders = ({
	transactionsGrouping,
}: GroupHeadersParams): {
	monthHeaders: string[];
	yearMonthMap: Map<string, string>;
} => {
	// generate month headers based on transaction data range
	const yearMonthMap = new Map<string, string>();

	// Generate the full range of months
	const fullMonthRange = getFullMonthRange(transactionsGrouping);

	// Update the yearMonthMap to include all months in the range
	fullMonthRange.forEach((YYYY_MM) => {
		if (!yearMonthMap.has(YYYY_MM)) {
			const [year, month] = YYYY_MM.split('-');
			const monthName = new Date(Number(year), Number(month) - 1).toLocaleString('default', {
				month: 'short',
			});
			yearMonthMap.set(YYYY_MM, `${monthName} ${year}`);
		}
	});

	// Ensure monthHeaders includes all months in the full range
	const monthHeaders = Array.from(yearMonthMap.entries())
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([YYYY_MM]) => YYYY_MM);

	return { monthHeaders, yearMonthMap };
};

// Generate the full range of months between the minimum and maximum dates
const getFullMonthRange = (transactions: TransactionGroup[]) => {
	if (transactions?.length === 0) return [];

	const dates = transactions.map((t) => new Date(t.year, t.month - 1));
	const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
	const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));

	const range: string[] = [];
	const currentDate = new Date(minDate);

	while (currentDate <= maxDate) {
		const YYYY_MM = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(
			2,
			'0'
		)}`;
		range.push(YYYY_MM);
		currentDate.setMonth(currentDate.getMonth() + 1);
	}

	return range;
};

export interface transactionGroupToRowsParams {
	groupKeys: string[];
	monthHeaders: string[];
	nodes: TransactionGroupItem[];
	parentGroups?: string[];
	t: TFunction;
}

export const transactionGroupToRows = ({
	groupKeys,
	monthHeaders,
	nodes,
	parentGroups = [],
	t,
}: transactionGroupToRowsParams): JsonObject[] => {
	const rows: JsonObject[] = [];

	function flattenTransactionGroupChildren(nodes: TransactionGroupItem[], parentGroups: string[]) {
		for (const node of nodes) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const row: Record<string, any> = {};

			// Fill group columns
			groupKeys.forEach((col, i) => {
				row[t(col)] =
					i === node.indent
						? node.isTotalRow
							? t(node.name as string) || t('total')
							: t(node.name as string) || ''
						: parentGroups[i] || '';
			});

			// add currency col
			row[t('currency')] = node.currency;

			// Fill month columns
			monthHeaders.forEach((month) => {
				row[month] = node[month] !== undefined ? node[month] : '';
			});

			// Fill total column
			row[t('total')] = node.total || 0;

			// Fill totalPaidFromDa column
			row[t('totalPaidFromDa')] = node.totalPaidFromDa || 0;

			rows.push(row);

			// Recurse into children
			if (node.children && node.children.length > 0) {
				flattenTransactionGroupChildren(
					node.children,
					groupKeys.map((_, idx) => (idx === node.indent ? '' : row[groupKeys[idx]]))
				);
			}
		}
	}

	flattenTransactionGroupChildren(nodes, parentGroups);
	return rows;
};
